import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  ListGroup, 
  Badge, 
  ProgressBar, 
  Tab, 
  Nav,
  Table
} from 'react-bootstrap';

const ForecastResults = ({ forecast, subject }) => {
  // Prepare topic frequency data for visualization
  const topicFrequencyData = forecast.forecast.topic_frequency || {};
  const sortedTopics = Object.entries(topicFrequencyData)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);
  
  const predictionConfidence = forecast.forecast.prediction_confidence || {};
  
  const getFrequencyBadgeColor = (frequency) => {
    if (frequency >= 70) return 'danger';
    if (frequency >= 50) return 'warning';
    if (frequency >= 30) return 'primary';
    return 'secondary';
  };
  
  const getConfidenceBadgeColor = (confidence) => {
    if (confidence >= 80) return 'success';
    if (confidence >= 60) return 'primary';
    if (confidence >= 40) return 'warning';
    return 'secondary';
  };

  const renderPatternAnalysis = () => {
    const analysis = forecast.forecast.pattern_analysis;
    
    if (typeof analysis === 'string') {
      return <p className="mb-0">{analysis}</p>;
    }
    
    if (typeof analysis === 'object' && analysis !== null) {
      return (
        <>
          {analysis.question_types && (
            <div className="mb-3">
              <h5>Question Types</h5>
              <ul>
                {analysis.question_types.map((type, idx) => (
                  <li key={idx}>{type}</li>
                ))}
              </ul>
            </div>
          )}
          
          {analysis.formats && (
            <div className="mb-3">
              <h5>Formats</h5>
              <ul>
                {analysis.formats.map((format, idx) => (
                  <li key={idx}>{format}</li>
                ))}
              </ul>
            </div>
          )}
          
          {analysis.trends && (
            <div className="mb-3">
              <h5>Trends</h5>
              <ul>
                {analysis.trends.map((trend, idx) => (
                  <li key={idx}>
                    {typeof trend === 'string' 
                      ? trend 
                      : trend.trend 
                        ? `${trend.trend}${trend.frequency ? ` (${trend.frequency * 100}%)` : ''}`
                        : JSON.stringify(trend)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      );
    }
    
    return <p className="mb-0">No pattern analysis available for the provided exam papers.</p>;
  };

  const renderRecommendations = () => {
    const recommendations = forecast.forecast.recommendations;
    
    if (typeof recommendations === 'string') {
      return <p>{recommendations}</p>;
    }
    
    if (typeof recommendations === 'object' && recommendations !== null) {
      return (
        <>
          {recommendations.focus_areas && (
            <div className="mb-3">
              <h5>Focus Areas</h5>
              <ul>
                {Array.isArray(recommendations.focus_areas) 
                  ? recommendations.focus_areas.map((area, idx) => (
                      <li key={idx}>{area}</li>
                    ))
                  : <li>{recommendations.focus_areas}</li>
                }
              </ul>
            </div>
          )}
          
          {recommendations.practice_problems && (
            <div className="mb-3">
              <h5>Practice Problems</h5>
              {Array.isArray(recommendations.practice_problems) 
                ? (
                  <ul>
                    {recommendations.practice_problems.map((problem, idx) => (
                      <li key={idx}>{problem}</li>
                    ))}
                  </ul>
                )
                : <p>{recommendations.practice_problems}</p>
              }
            </div>
          )}
          
          {recommendations.revision_strategy && (
            <div className="mb-3">
              <h5>Revision Strategy</h5>
              {Array.isArray(recommendations.revision_strategy) 
                ? (
                  <ul>
                    {recommendations.revision_strategy.map((strategy, idx) => (
                      <li key={idx}>{strategy}</li>
                    ))}
                  </ul>
                )
                : <p>{recommendations.revision_strategy}</p>
              }
            </div>
          )}
        </>
      );
    }
    
    return <p>No specific recommendations available based on the provided exam papers.</p>;
  };

  // Check for historical questions
  const hasHistoricalQuestions = 
    forecast.forecast.historical_questions && 
    forecast.forecast.historical_questions.length > 0;

  // If we don't have historical_questions but have repeating_questions, use those
  const historicalQuestions = forecast.forecast.historical_questions || 
    (forecast.forecast.repeating_questions ? 
      forecast.forecast.repeating_questions.map(q => ({ question: q, year: 'Unknown' })) : 
      []);

  return (
    <div>
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h3>Exam Forecast for {subject}</h3>
              <div className="text-muted">Based on {forecast.exam_count} previous exam papers</div>
            </div>
            <div>
              <Badge bg="info" className="p-2">AI: {forecast.ai_provider}</Badge>
            </div>
          </div>
          
          {/* Historical Questions highlight section */}
          {historicalQuestions.length > 0 && (
            <Card className="mb-4 border-danger">
              <Card.Header className="bg-danger text-white">
                <h4 className="mb-0">High-Priority Historical Questions</h4>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th width="15%">Year</th>
                      <th>Question</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicalQuestions.slice(0, 5).map((item, index) => (
                      <tr key={index}>
                        <td>{item.year || 'Past Paper'}</td>
                        <td>{item.question}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {historicalQuestions.length > 5 && (
                  <div className="text-center mt-2">
                    <Badge bg="secondary">+ {historicalQuestions.length - 5} more questions</Badge>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
          
          <Tab.Container defaultActiveKey="predicted-topics">
            <Row>
              <Col sm={3}>
                <Nav variant="pills" className="flex-column mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="predicted-topics">Predicted Topics</Nav.Link>
                  </Nav.Item>
                  {historicalQuestions.length > 0 && (
                    <Nav.Item>
                      <Nav.Link eventKey="historical-questions">
                        Historical Questions
                        <Badge bg="danger" pill className="ms-2">{historicalQuestions.length}</Badge>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  <Nav.Item>
                    <Nav.Link eventKey="topic-frequency">Topic Frequency</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="pattern-analysis">Pattern Analysis</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="recommendations">Recommendations</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col sm={9}>
                <Tab.Content>
                  <Tab.Pane eventKey="predicted-topics">
                    <h4 className="mb-3">Topics Likely to Appear in Future Exams</h4>
                    <ListGroup className="mb-4">
                      {forecast.forecast.predicted_topics && 
                        forecast.forecast.predicted_topics.map((topicItem, index) => (
                          <ListGroup.Item 
                            key={index}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <strong>{typeof topicItem === 'object' ? topicItem.topic : topicItem}</strong>
                            </div>
                            <div>
                              {typeof topicItem === 'object' && topicItem.confidence && (
                                <Badge 
                                  bg={getConfidenceBadgeColor(topicItem.confidence)}
                                  className="ms-2"
                                >
                                  {topicItem.confidence}% confidence
                                </Badge>
                              )}
                              {!topicItem.confidence && predictionConfidence[typeof topicItem === 'string' ? topicItem : topicItem.topic] && (
                                <Badge 
                                  bg={getConfidenceBadgeColor(predictionConfidence[typeof topicItem === 'string' ? topicItem : topicItem.topic])}
                                  className="ms-2"
                                >
                                  {predictionConfidence[typeof topicItem === 'string' ? topicItem : topicItem.topic]}% confidence
                                </Badge>
                              )}
                            </div>
                          </ListGroup.Item>
                        ))
                      }
                    </ListGroup>
                  </Tab.Pane>
                  
                  {/* Historical Questions Tab */}
                  {historicalQuestions.length > 0 && (
                    <Tab.Pane eventKey="historical-questions">
                      <h4 className="mb-3">Historical Exam Questions</h4>
                      <Table striped bordered hover responsive>
                        <thead className="table-dark">
                          <tr>
                            <th width="10%">#</th>
                            <th width="15%">Year</th>
                            <th>Question</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historicalQuestions.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{item.year || 'Past Paper'}</td>
                              <td>{item.question}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Tab.Pane>
                  )}
                  
                  <Tab.Pane eventKey="topic-frequency">
                    <h4 className="mb-3">Most Frequent Topics in Previous Exams</h4>
                    <ListGroup className="mb-4">
                      {sortedTopics.map(([topic, frequency], index) => (
                        <ListGroup.Item key={index}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <strong>{topic}</strong>
                            <Badge 
                              bg={getFrequencyBadgeColor(frequency * 100)}
                              className="ms-2"
                            >
                              {Math.round(frequency * 100)}%
                            </Badge>
                          </div>
                          <ProgressBar 
                            now={frequency * 100} 
                            variant={getFrequencyBadgeColor(frequency * 100)}
                            style={{ height: '8px' }}
                          />
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Tab.Pane>
                  
                  <Tab.Pane eventKey="pattern-analysis">
                    <h4 className="mb-3">Exam Pattern Analysis</h4>
                    <Card className="bg-light">
                      <Card.Body>
                        {renderPatternAnalysis()}
                      </Card.Body>
                    </Card>
                    
                    <h5 className="mt-4 mb-2">Recurring Topics</h5>
                    <div className="mb-4">
                      {forecast.forecast.recurring_topics && forecast.forecast.recurring_topics.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {forecast.forecast.recurring_topics.map((topic, index) => (
                            <Badge 
                              key={index} 
                              bg="info" 
                              className="p-2"
                            >
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted">No recurring topics identified</p>
                      )}
                    </div>
                  </Tab.Pane>
                  
                  <Tab.Pane eventKey="recommendations">
                    <h4 className="mb-3">Study Recommendations</h4>
                    <Card className="bg-light mb-4">
                      <Card.Body>
                        {renderRecommendations()}
                      </Card.Body>
                    </Card>
                    
                    <Card className="bg-light">
                      <Card.Body>
                        <h5>Suggested Study Plan</h5>
                        <ol>
                          <li>Focus on high-confidence predicted topics first</li>
                          <li>Master the historical questions - these are likely to reappear</li>
                          <li>Review recurring topics thoroughly</li>
                          <li>Practice questions in the formats identified in the pattern analysis</li>
                          <li>Create practice tests based on the exam pattern analysis</li>
                        </ol>
                      </Card.Body>
                    </Card>
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Card.Body>
      </Card>
      
      <div className="text-center text-muted">
        <p>
          <small>
            Note: This forecast is based on analysis of previous exam patterns and is not a guarantee of future exam content.
            Always prepare comprehensively for your exams.
          </small>
        </p>
      </div>
    </div>
  );
};

export default ForecastResults;